import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Moves, Offers } from '@gql/base-types';
import { Alert, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useRooftop } from '@store';
import {
  OrderDateRangeStrip,
  OrderFilterMultiselect,
  ORDER_OFFER_STATUS_OPTIONS,
  VALET_DATE_RANGE_PRESETS,
  resolveEffectiveDateWindow,
  useValetOrdersStore,
  type OrderDateRangePresetKey,
  type OrderIndexDateRange,
  type OrderOfferStatusKey as OfferStatusFilterKey,
} from '@hopdrive/feature-order-index';
import {
  useGetMoveForDispatchLazyQuery,
  type GetMoveForDispatchQuery,
} from '@features/moves/queries/getMoveForDispatch.query.generated';
import AssignOrUnassignMoveModal from '@components/Modals/AssignOrUnassignMoveModal';
import { canAssignOrUnassignMove } from '@hopdrive/feature-driver-assignment';
import { useViewAssignmentsReport, VIEW_ASSIGNMENTS_MOVE_CAP } from '../hooks/useViewAssignmentsReport';
import ViewAssignmentsKpis from '../components/ViewAssignmentsKpis';
import ViewAssignmentsTable from '../components/ViewAssignmentsTable';
import type { ViewAssignmentsRow } from '../utils/viewAssignmentsRows';

/** The board's own window shape: preset plus the custom range's bounds. */
type ReportWindow = OrderIndexDateRange;

/** Case-insensitive substring match across searchable fields. */
const matchesSearch = (row: ViewAssignmentsRow, search: string): boolean => {
  const term = search.toLowerCase();
  return (
    row.displayName.toLowerCase().includes(term) ||
    row.driveMoveIds.some(id => String(id).includes(term)) ||
    (row.driver?.display_name ?? '').toLowerCase().includes(term) ||
    (row.consumerName ?? '').toLowerCase().includes(term)
  );
};

/**
 * The "View Assignments" offer report. One row per order over a date range, classified by
 * the SAME `resolveDriverAssignment` the dashboard board uses — so the report agrees with
 * the board for any overlapping window (see viewAssignments.query.gql for why the window
 * predicate is the board's, verbatim).
 */
const ViewAssignmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const rooftop = useRooftop();
  const customerId = rooftop?.id ?? 0;
  const rooftopTier = rooftop?.tier ?? null;

  // Seed the report's window from the dashboard's current (persisted) window ONCE, via a
  // lazy initializer reading the store without subscribing — thereafter the report owns
  // its own window, so changing the date here never touches the board.
  //
  // `.getState()` here is a STATIC read on the Zustand store object (the hook double-duties
  // as the store; no hook call, no subscription — legal inside the initializer). If the
  // store module ever stops exporting the raw store through the hook, this is the line
  // that needs its export.
  const [reportWindow, setReportWindow] = React.useState<ReportWindow>(() => useValetOrdersStore.getState().dateRange);

  // Client-side filters — these narrow the already-fetched rows, not the query.
  const [statusFilter, setStatusFilter] = React.useState<OfferStatusFilterKey[]>([]);
  const [search, setSearch] = React.useState('');

  // Assign driver modal state — typed by the query that fills it, so the modal's
  // required props stay checked against what this page actually fetched.
  const [assignModalOpen, setAssignModalOpen] = React.useState(false);
  // Set when the authoritative re-check in handleAssignDriver refuses a row the table had
  // enabled — a race, not a normal state, so it clears on the next successful open.
  const [staleAssignNotice, setStaleAssignNotice] = React.useState<string | null>(null);
  const [assignModalMove, setAssignModalMove] = React.useState<GetMoveForDispatchQuery['moves_by_pk'] | null>(null);
  // The clicked row's live offers, handed to the modal like the dashboard does: the modal
  // decides Assign vs Reassign/Unassign from move.driver_id OR these, and an offered
  // move has no driver_id until the driver accepts.
  const [assignModalOffers, setAssignModalOffers] = React.useState<ViewAssignmentsRow['offers']>([]);
  // network-only: the gate in handleAssignDriver is the AUTHORITATIVE re-check of a move
  // that may have finished since the report drew its row. Apollo's cache is shared with
  // the assign modal's own copy of this query, so a cache-first read could answer from a
  // snapshot taken before the move moved on and let the gate pass a delivered move.
  const [getMoveForDispatch] = useGetMoveForDispatchLazyQuery({ fetchPolicy: 'network-only' });

  const effective = resolveEffectiveDateWindow({
    preset: reportWindow.preset,
    startDate: reportWindow.startDate,
    endDate: reportWindow.endDate,
  });

  const { report, loading, initialLoading, refetch: refetchReport } = useViewAssignmentsReport({
    customerId,
    rooftopTier,
    start: effective.startIso,
    end: effective.endIso,
  });

  // Apply client-side filters to the fetched rows.
  const filteredRows = React.useMemo(() => {
    let rows = report.rows;
    if (statusFilter.length) rows = rows.filter(row => statusFilter.includes(row.status));
    if (search.trim()) rows = rows.filter(row => matchesSearch(row, search.trim()));
    return rows;
  }, [report.rows, statusFilter, search]);

  // Navigate to move details for the first drive move in the order.
  const handleViewDetails = React.useCallback(
    (row: ViewAssignmentsRow) => {
      const moveId = row.driveMoveIds[0];
      if (moveId) navigate(`/moves/${moveId}`);
    },
    [navigate]
  );

  // Fetch move data and open the assign/reassign modal.
  //
  // The row's own `assignable` already disabled this entry for a move past pickup, but the
  // report is a plain query with no subscription: a move can finish between the fetch that
  // drew the row and the click. So the gate runs AGAIN here, on the move we just read — and
  // this is the authoritative reading, the same `getMoveForDispatch` shape the details page
  // gates on, parent and ride children included. Nothing downstream re-checks: the modal
  // does not, so a dispatch from here would otherwise land on a delivered move.
  const handleAssignDriver = React.useCallback(
    async (row: ViewAssignmentsRow) => {
      const moveId = row.driveMoveIds[0];
      if (!moveId) return;

      // A failed or empty read is said out loud too — silently returning left the menu
      // entry looking broken, the same failure the stale notice below exists for.
      let move: GetMoveForDispatchQuery['moves_by_pk'] | null | undefined;
      try {
        const { data, error } = await getMoveForDispatch({ variables: { moveId } });
        move = error ? null : data?.moves_by_pk;
      } catch {
        move = null;
      }
      if (!move) {
        setStaleAssignNotice(
          `Couldn't load order ${row.displayName} to assign it — it may have changed while this report was open. Refresh the date range and try again.`
        );
        return;
      }

      if (!canAssignOrUnassignMove(move)) {
        // Say so rather than no-op: a menu entry that was enabled a moment ago and now does
        // nothing reads as a broken button, which is the failure this whole gate is about.
        setStaleAssignNotice(
          `Order ${row.displayName} can no longer be assigned — it moved on while this report was open. Refresh the date range for its current state.`
        );
        return;
      }

      setStaleAssignNotice(null);
      setAssignModalOffers(row.offers);
      setAssignModalMove(move);
      setAssignModalOpen(true);
    },
    [getMoveForDispatch]
  );

  // Refetch on close so the row reflects an assign/unassign now, not at the next
  // 60-second poll. (A cancel refetches too — cheap, and harmless.)
  const handleAssignModalClose = React.useCallback(() => {
    setAssignModalOpen(false);
    setAssignModalMove(null);
    setAssignModalOffers([]);
    refetchReport();
  }, [refetchReport]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' sx={{ mb: 2 }}>
        View Assignments
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <OrderDateRangeStrip
          piece={{
            presets: VALET_DATE_RANGE_PRESETS,
            preset: reportWindow.preset,
            customStartIso: reportWindow.startDate || null,
            customEndIso: reportWindow.endDate || null,
            onPresetChange: preset => setReportWindow(current => ({ ...current, preset: preset as OrderDateRangePresetKey })),
            onCustomRangeChange: (startDate, endDate) => setReportWindow(current => ({ ...current, startDate: startDate ?? '', endDate: endDate ?? '' })),
          }}
        />
        <OrderFilterMultiselect
          piece={{
            label: 'Status',
            // Live counts over the fetched rows, the standing filter's version of the metric tiles' counts.
            options: ORDER_OFFER_STATUS_OPTIONS.map(option => ({ ...option, count: report.counts[option.value] })),
            selected: statusFilter,
            onChange: selected => setStatusFilter(selected),
          }}
        />
        <TextField
          size='small'
          label='Search'
          placeholder='Order, driver, guest...'
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 220 }}
        />
      </Box>

      {staleAssignNotice && (
        <Alert severity='info' sx={{ mb: 2 }} onClose={() => setStaleAssignNotice(null)}>
          {staleAssignNotice}
        </Alert>
      )}

      {report.truncated && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Showing the first {VIEW_ASSIGNMENTS_MOVE_CAP.toLocaleString()} moves in this range — the tiles and table reflect
          this loaded set, not the full window. Narrow the date range for complete counts.
        </Alert>
      )}

      <ViewAssignmentsKpis
        counts={report.counts}
        // Not `loading`: that is also true on every background poll, and the tiles
        // flashed to their loading state once a minute.
        loading={initialLoading}
        selected={statusFilter}
        onToggle={key =>
          setStatusFilter(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
        }
      />

      {loading && !report.rows.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredRows.length ? (
        <ViewAssignmentsTable
          rows={filteredRows}
          onViewDetails={handleViewDetails}
          onAssignDriver={handleAssignDriver}
        />
      ) : report.rows.length ? (
        <Typography color='text.secondary' sx={{ py: 4, textAlign: 'center' }}>
          No orders match the current filters.
        </Typography>
      ) : (
        <Typography color='text.secondary' sx={{ py: 4, textAlign: 'center' }}>
          No offer activity in this date range.
        </Typography>
      )}

      {/* The modal's prop is the WIDE generated table type while every caller
          hands it a query selection; the conversion is at this one boundary so
          the state above stays checked against what this page actually fetched.
          getMoveForDispatch selects every field the modal's content reads. */}
      <AssignOrUnassignMoveModal
        open={assignModalOpen}
        onClose={handleAssignModalClose}
        moveInput={assignModalMove as unknown as Moves | null}
        offersInput={assignModalOffers as unknown as Offers[]}
      />
    </Box>
  );
};

export default ViewAssignmentsPage;
